import { cn } from '@/lib/cn';
import type { Card } from '@/lib/qcp/protocol';

/** Map numeric rank to display string. */
function rankLabel(r: number): string {
  if (r === 11) return 'J';
  if (r === 12) return 'Q';
  if (r === 13) return 'K';
  if (r === 14) return 'A';
  return String(r);
}

/** Map suit char to Unicode glyph. */
function suitGlyph(s: Card['s']): string {
  switch (s) {
    case 'c': return '♣';
    case 'd': return '♦';
    case 'h': return '♥';
    case 's': return '♠';
  }
}

/** True for red suits (diamonds + hearts). */
function isRed(s: Card['s']): boolean {
  return s === 'd' || s === 'h';
}

interface PlayingCardProps {
  card?: Card;
  /** Show card back instead of face. */
  faceDown?: boolean;
  /** Render with a bright brand ring to indicate selection / picked-up. */
  selected?: boolean;
  /** Render with a dim brand ring to indicate "placed this hand" (not picked). */
  pending?: boolean;
  /** Render with a red ring — the card being discarded this round. */
  discard?: boolean;
  onClick?: () => void;
}

/**
 * A single playing card tile sized for mobile OFC boards.
 *
 * - Face: rank (top-left) + suit glyph (centre) in the card's colour.
 * - Face-down: a dark back-pattern tile.
 * - Empty placeholder: a dashed slot.
 * - Selected: brand-coloured ring overlay.
 *
 * Uses QLASH Tailwind surface tokens (bg-bg-secondary, border-border,
 * text-fg-muted) and brand tokens (ring-brand) — no hard-coded colours.
 */
export function PlayingCard({ card, faceDown = false, selected = false, pending = false, discard = false, onClick }: PlayingCardProps) {
  const interactive = Boolean(onClick);

  // Empty placeholder slot ─────────────────────────────────────────────────
  if (!card && !faceDown) {
    return (
      <div
        className={cn(
          'flex h-[52px] w-[38px] items-center justify-center rounded-md border border-dashed border-border',
          selected && 'ring-2 ring-brand',
          interactive && 'cursor-pointer active:scale-95',
        )}
        onClick={onClick}
        role={interactive ? 'button' : undefined}
      />
    );
  }

  // Face-down back ─────────────────────────────────────────────────────────
  if (faceDown) {
    const content = (
      <div
        className={cn(
          'flex h-[52px] w-[38px] items-center justify-center rounded-md bg-bg-secondary',
          'border border-border',
          // Crosshatch-like visual via repeating-linear-gradient via bg-[size] trick
          'bg-[repeating-linear-gradient(45deg,#2f3434_0px,#2f3434_2px,transparent_2px,transparent_8px)]',
          selected && 'ring-2 ring-brand',
        )}
      />
    );
    if (interactive) {
      return (
        <button type="button" onClick={onClick} className="block" aria-label="card back">
          {content}
        </button>
      );
    }
    return content;
  }

  // Face-up card ───────────────────────────────────────────────────────────
  // card is guaranteed non-null here (we returned early for the empty case)
  const rank = rankLabel(card!.r);
  const glyph = suitGlyph(card!.s);
  const red = isRed(card!.s);

  const content = (
    <div
      className={cn(
        'relative flex h-[52px] w-[38px] flex-col rounded-md bg-bg-secondary',
        'border border-border select-none',
        red ? 'text-red-400' : 'text-fg',
        selected && 'ring-2 ring-brand',
        pending && !selected && 'ring-1 ring-brand/40',
        discard && 'ring-2 ring-danger opacity-80',
        interactive && 'cursor-pointer active:scale-95 transition-transform',
      )}
    >
      {/* Top-left rank */}
      <span className="absolute left-[3px] top-[2px] text-[11px] font-bold leading-none">
        {rank}
      </span>
      {/* Centre suit glyph */}
      <span className="m-auto text-[18px] leading-none mt-[10px]">{glyph}</span>
      {/* Bottom-right rank (rotated 180°) */}
      <span className="absolute bottom-[2px] right-[3px] text-[11px] font-bold leading-none rotate-180">
        {rank}
      </span>
    </div>
  );

  if (interactive) {
    return (
      <button type="button" onClick={onClick} className="block" aria-label={`${rank}${glyph}`}>
        {content}
      </button>
    );
  }
  return content;
}
